/**
 * The sign-in page's backdrop — the four programme countries, drawn live (client instruction,
 * 2026-09-08).
 *
 * The entry page's left half used to be IFDC's photograph. The client's line for this round is
 * that it should be *"a map of the operational countries"*, and this is that map: the
 * deployment's default basemap with the level-0 outlines from `/hierarchy/units` over it,
 * fitted to the programme extent, under the same translucent film the photograph had and
 * behind the same copy, word for word. Nothing else on the page changed for it, and no asset
 * was deleted — `gateAssets.BACKDROP` still resolves `backdrop.jpg`; this page simply stops
 * asking for it (see `SignInGate`).
 *
 * ## It is scenery, and every option below says so
 *
 * A reader at the gate has not asked for a map and cannot yet do anything with one. So every
 * handler Leaflet ships is off — drag, wheel, double-click, box, touch, keyboard — there is no
 * zoom control, and the container is `aria-hidden` with `pointer-events:none` over it, so a
 * pointer travelling to the email field never lands on a tile and a screen reader never meets
 * a widget with no purpose. What survives is the attribution: CARTO's and OSM's terms require
 * the credit wherever their tiles are painted, and a licence condition is not chrome.
 *
 * ## The one query, and the reason it costs nothing
 *
 * `queryKeys.units('admin', 0, 'ALL', 'z5')` is **the Overview's own key**, deliberately: the
 * next screen in the session (`OverviewMap`) asks for exactly this payload, and TanStack Query
 * hands it the cached one. The gate therefore moves the request earlier in the session rather
 * than adding one to it.
 *
 * ## If the API is not there, this renders nothing at all
 *
 * Which is the whole of the degradation, and it is deliberate. `#gateArt` carries a soil-green
 * gradient under this layer for exactly the case the photograph had — no file, no network, a
 * cold server — and the film and the copy sit on it unchanged. A basemap with no countries on
 * it would be a *worse* answer than the gradient: it is a map of somewhere, drawn at a zoom
 * nobody chose, with nothing of the programme in it.
 *
 * ## The land cover, and why it now moves (client instruction, 2026-09-11)
 *
 * > *"We also anticipated a dynamic background map (preferably the raster landcover with
 * > programme country boundaries). We think this page gives a first impression of the work we
 * > have done so far and won't want to introduce a first sour taste."*
 *
 * …and then, on the round that followed: *"honestly no one is interested in what changed and
 * what didn't change, they just want to see that dynamism playing out, if we can lets do
 * that."*
 *
 * So the panel plays the eight published epochs (2017–2024) as a dissolve, with a year marker
 * that advances beside it. The frames are **committed artwork**, not live tiles — see
 * `gateEpochs`, which holds both the assets and the loop's arithmetic, and
 * `manage.py render_gate_epochs`, which bakes them from the published COGs.
 *
 * The second sentence of the first instruction is still the constraint, and it is still what
 * shapes the code. On the one page every visitor sees, a half-drawn raster is worse than no
 * raster. So the stack goes into a pane the stylesheet holds at `opacity:0`, every frame is
 * decoded invisibly *before* anything is added to the map, and the page commits to one of
 * exactly two approved pictures — the whole animation, or the basemap and the four filled
 * countries that shipped before it. There is no reveal-anyway path and no partial one.
 *
 * Replacing the tile layer rather than layering the animation over it is deliberate for the
 * same reason: a live raster that hands over to a baked one is a *third* picture, and a
 * handover the reader can see is the flicker this panel promised not to make.
 *
 * ## What is allowed to move, and what stops it
 *
 * The reveal is still two class-gated CSS transitions in `base.css`. The loop is Web
 * Animations — `opacity` and `transform` only, so every one of them runs in the compositor —
 * built from `gateEpochs`' timeline functions so the raster and the year marker are cut from
 * one clock. It runs three times and then rests on the newest epoch, which is the same
 * at-rest DOM the still page had.
 *
 * `prefers-reduced-motion` is read through `window.matchMedia` **guarded**, because it is
 * undefined in this project's jsdom and an unguarded call would take every gate unit test with
 * it. Under reduced motion no animation is constructed at all and the panel is the still,
 * composed newest-epoch frame with the marker resting on its last year — the state the
 * elements' own inline styles already carry, so there is nothing to undo. The loop is also
 * paused whenever the tab is hidden.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import L from 'leaflet';

import { fetchLayers, fetchUnits, queryKeys, type UnitsFC } from '../../lib/api';
import { useAppStore } from '../../lib/state';
import { resolveBasemap, useBasemaps } from '../map/basemaps';
import { HOME_BOUNDS } from '../map/panes';
import {
  dotKeyframes,
  EPOCHS,
  type EpochFrame,
  fillKeyframes,
  ITERATIONS,
  layerKeyframes,
  loopMs,
  MIN_FRAMES,
  odometerKeyframes,
  sweepKeyframes,
} from './gateEpochs';

/**
 * How long the frames get to arrive *complete* before we give up on them and leave the page at
 * its previous state — basemap, four filled countries — which is the page the client approved.
 *
 * Measured against the live stack: eight WebPs, 17–19 kB each, 143 kB in total, all eight
 * decoded in 90–210 ms warm at 1280, 1440 and 1920 (one request per frame, no tile grid to
 * compute). 4000ms is twenty times that, and it is the figure the tile layer used before, kept
 * so a slow deployment behaves the way it already did. Re-measure it against the real
 * deployment — and *raise* it if it is tight there, never remove it.
 *
 * **It abandons; it does not merely stop waiting.** "Stop waiting" would mean judging the
 * animation on the frames that happened to arrive, and the one case where we *know* the set is
 * incomplete is the case where the deadline fired.
 */
const REVEAL_DEADLINE_MS = 4000;

/**
 * The gap between adding the overlays and switching the pane on.
 *
 * Every frame is already decoded by then (that is what the preload is for), so this is one
 * paint's worth of grace for Leaflet's own `<img>` elements to be laid out and drawn rather
 * than a wait for data. The pane is at `opacity:0` throughout, so it is invisible either way.
 */
const MOUNT_SETTLE_MS = 200;

/**
 * How long the reveal's own fade lasts — `#gateArt .gate-lc-pane`'s transition in `base.css`.
 *
 * The loop is delayed by this, so the rewind cannot start while the panel is still fading in.
 * Two motions at once on the first second of the sign-in page is one motion too many, and the
 * opening hold exists precisely so the reader sees the still picture first. **Keep it equal to
 * the stylesheet's transition-duration**; it is the one number that lives in both places.
 */
const REVEAL_FADE_MS = 1100;

/**
 * The square a frame is downsampled into when we ask "did this one paint anything at all?".
 *
 * Small because the question is a boolean, and a 32×32 box means each destination pixel
 * averages ~570 source pixels of an 896×684 frame — so a frame that is 54 % opaque, which is
 * what the render command measures and enforces, answers on the first sample it takes.
 */
const PIXEL_PROBE_PX = 32;

/**
 * How far the committed frames' bbox may sit from the outlines actually drawn, as a share of
 * the programme's own span on each axis, before the animation declines to mount.
 *
 * This is not hypothetical insurance. The published footprint changed by **6× in area** on
 * 2026-09-08; a committed frame set stretched over a re-clipped map is a wrong map, drawn
 * under live country borders, in front of a GIS client. 2 % is 0.56° of longitude and 0.41° of
 * latitude — far above the z5 simplification's disagreement with the §1a outline the frames
 * were rendered to (measured: 0.001°, about 115 m) and far below any real re-clip.
 */
const BBOX_TOLERANCE = 0.02;

/** What `L.geoJSON` will take, without importing a `@types/geojson` we do not have. */
type GeoJsonInput = Parameters<typeof L.geoJSON>[0];

/**
 * The outlines, styled for a panel that is about to be darkened by 44-74 %.
 *
 * The Overview's admin line is a near-black warm stroke, which is right on a light basemap in
 * a bright card and invisible under this page's film. Here the countries are a pale wash with
 * a warm ivory edge — light-on-dark, the inverse of the same idea, so the four shapes still
 * read as shapes once the wash is over them.
 *
 * **`fillColor` and `fillOpacity` here are inert.** `base.css` owns both (search
 * `path.gate-country`) so that the fill can retreat out from under the land cover as a real CSS
 * property transition — a CSS author rule outranks the presentation attributes Leaflet writes.
 * They are left in place because they are what the SVG carries before the stylesheet applies,
 * but a future `layer.setStyle({ fillOpacity })` will appear to do nothing: change the
 * stylesheet instead. `color` and `weight` are still Leaflet's.
 */
const GATE_OUTLINE: L.PathOptions = {
  color: '#FFF3D2',
  weight: 2.4,
  opacity: 0.95,
  fillColor: '#BFE292',
  fillOpacity: 0.46,
};

/**
 * Where the programme is, in the only two shapes the land cover's reveal rule needs.
 *
 * Both are read off the `L.GeoJSON` layer this component already builds, rather than derived a
 * second time from the raw coordinates: one projection path, so the rectangle Leaflet is given
 * as tile `bounds` and the boxes a failed tile is tested against can never disagree with the
 * outlines actually drawn.
 *
 * * `all` — the four countries' bounding **rectangle**. What Leaflet gets as `bounds`, and what
 *   the map is fitted to.
 * * `parts` — one box per country. Coarser than the outlines (a bounding box of Mali contains a
 *   lot of Mauritania) and deliberately so: it is used to decide whether a *failed* tile covers
 *   programme ground, and erring towards "yes" errs towards the approved fallback.
 */
type Footprint = { all: L.LatLngBounds; parts: L.LatLngBounds[] };

export function GateMap() {
  const containerRef = useRef<HTMLDivElement | null>(null);
  /** The year marker, so the loop can find its three moving parts without a query on document. */
  const markerRef = useRef<HTMLDivElement | null>(null);
  /** The light sweep, for the same reason: the loop drives it, so the loop must be able to find it. */
  const sweepRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<L.Map | null>(null);
  const { t } = useTranslation();
  const [map, setMap] = useState<L.Map | null>(null);
  /** The programme's footprint, taken from the geometry this page already fetched. */
  const [footprint, setFootprint] = useState<Footprint | null>(null);
  const basemap = useAppStore((s) => s.basemap);
  const basemaps = useBasemaps();
  const role = useAppStore((s) => s.role);

  /**
   * `retry: false`: a gate that spends three backoffs waiting for a decorative payload is a
   * gate that paints its fallback a second too late to be a fallback. One try, then the
   * gradient.
   */
  const { data: outlines } = useQuery<UnitsFC>({
    queryKey: queryKeys.units('admin', 0, 'ALL', 'z5'),
    queryFn: () => fetchUnits('admin', 0, 'ALL', 'z5'),
    retry: false,
    staleTime: 5 * 60_000,
  });

  /** Level-0 features only, and only if there are any: see the module comment. */
  const countries = useMemo(() => {
    const features = outlines?.features ?? [];
    return features.length ? outlines : null;
  }, [outlines]);

  /**
   * The catalogue, on the Overview's own key — the same argument as the units query above. The
   * next screen in the session reads `queryKeys.layers(role)` with the bare `fetchLayers` (it
   * takes the language out of the key itself), so the gate moves that request earlier rather
   * than adding one. Role is `'public'` before sign-in and stays `'public'` through "Continue
   * as guest", so the key this warms is the key the Overview reads a moment later. A bespoke
   * key or a different role here would turn a prefetch into pure overhead on the one page every
   * visitor loads.
   *
   * **Its result is deliberately unused.** The land cover used to be read out of this payload
   * as a tile template; since 2026-09-11 it is committed artwork with its own baked CC-BY
   * credit (`gateEpochs`), so the backdrop no longer depends on a catalogue request succeeding
   * — a licence condition that can go missing on a bad network is not a licence condition. The
   * query stays because the *prefetch* was always the better half of the argument for it.
   */
  useQuery({
    queryKey: queryKeys.layers(role),
    queryFn: fetchLayers,
    retry: false,
    staleTime: 5 * 60_000,
  });

  /**
   * The years actually playing, set when the stack mounts and `null` whenever it is not —
   * which is what draws the year marker. It is state rather than a ref because the marker is
   * React's to render: it must not exist on the approved fallback picture, where there is no
   * raster and therefore no year to name.
   */
  const [playing, setPlaying] = useState<number[] | null>(null);

  // --- the map, created once the geometry it is for has arrived ---------------
  useEffect(() => {
    const el = containerRef.current;
    if (!el || !countries || mapRef.current) return;
    const m = L.map(el, {
      zoomControl: false,
      // The tile providers' credit, which is a licence condition rather than a control.
      attributionControl: true,
      dragging: false,
      touchZoom: false,
      scrollWheelZoom: false,
      doubleClickZoom: false,
      boxZoom: false,
      keyboard: false,
      inertia: false,
      // The same reason the Overview's map takes it: `fitBounds` rounds *down* to the snap, and
      // a quarter of a zoom level thrown away here is the programme not filling its panel.
      zoomSnap: 0,
    });
    m.attributionControl.setPrefix(false);
    // The land cover's own pane. 250 sits above Leaflet's tilePane (200, the basemap) and below
    // its overlayPane (400, the country SVG), so the ivory borders stay drawn *on* the data
    // rather than under it. The explicit class is so the stylesheet can address the pane by a
    // name we own: `createPane` strips the trailing "Pane" when it builds its own class, which
    // is a classic silent mis-selector.
    const lcPane = m.createPane('gateLc');
    lcPane.style.zIndex = '250';
    lcPane.style.pointerEvents = 'none';
    lcPane.classList.add('gate-lc-pane');
    m.fitBounds(HOME_BOUNDS);
    mapRef.current = m;
    setMap(m);
    return () => {
      m.remove();
      mapRef.current = null;
      setMap(null);
    };
  }, [countries]);

  /** The panel's width is a share of the viewport, so the frame moves under the map. */
  useEffect(() => {
    const el = containerRef.current;
    if (!map || !el || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(() => map.invalidateSize({ animate: false }));
    observer.observe(el);
    return () => observer.disconnect();
  }, [map]);

  // --- the deployment's own basemap ------------------------------------------
  useEffect(() => {
    if (!map) return;
    const def = resolveBasemap(basemaps, basemap);
    const layer = L.tileLayer(def.url, { attribution: def.attr, maxZoom: def.maxZoom }).addTo(map);
    layer.bringToBack();
    return () => {
      map.removeLayer(layer);
    };
  }, [map, basemap, basemaps]);

  // --- the four countries, and the fit to them --------------------------------
  useEffect(() => {
    if (!map || !countries) return;
    const layer = L.geoJSON(countries as unknown as GeoJsonInput, {
      style: () => GATE_OUTLINE,
      interactive: false,
      onEachFeature: (feature, shape) => {
        const props = (feature?.properties ?? {}) as { country?: string };
        shape.on('add', () => {
          const el = shape instanceof L.Path ? shape.getElement() : null;
          el?.setAttribute('data-country', String(props.country ?? ''));
          el?.classList.add('gate-country');
        });
      },
    }).addTo(map);
    const bounds = layer.getBounds();
    // The programme's footprint, derived rather than hard-coded. The land-cover COG is cut
    // against the §1a dissolve of exactly these four countries, so the geometry this page
    // already fetched *is* the raster's ground — one source of truth, and it survives a COG
    // whose footprint changes. `parts` is the same read, one box per country: see `Footprint`.
    const parts = layer
      .getLayers()
      .map((shape) => (shape instanceof L.Polygon ? shape.getBounds() : null))
      .filter((b): b is L.LatLngBounds => !!b && b.isValid());
    setFootprint(bounds.isValid() ? { all: bounds, parts } : null);
    map.invalidateSize({ animate: false });
    // A proportion of the extent's own size rather than a fixed pixel inset, so it means the same
    // thing whether the panel is 693px wide on a 1440 or 340px tall on a phone.
    //
    // **2.5%, down from 6% (client + review, 2026-09-11).** The review asked for a tighter fit —
    // *"less dead surround = more land cover"* — and the gallery reached it by zooming ~1.17x,
    // which cut Mali's western tip and a slice of eastern Niger off the page. Both the brand and
    // the client reviewer rejected that outright, and they are right: on a hero whose stated job
    // is "programme country boundaries", a cut national border is the first thing a GIS client
    // sees.
    //
    // The measurement that settles it: `fitBounds` fits the *binding* axis, and here that is
    // width — the four countries' rectangle is 1.35 wide to 1 tall in Web Mercator against a
    // panel that is 0.82 wide to 1 tall. So at 6% the outlines already sit 37px from the left
    // and right edges of a 693px panel (5.3% of slack) while 391px, 45% of the panel's height,
    // is empty above and below them. Zooming cannot convert that empty height into land cover
    // without spending width the programme does not have. 2.5% is the tightest fit that still
    // leaves a real margin — measured 16px each side at 1440x900, coverage 48.9% -> 55.9% — and
    // the rest of the reviewers' complaint about the surround is answered where it actually
    // lives, in what the surround looks like (`#gateArt .leaflet-tile-pane`, base.css).
    //
    // Do not raise this past ~3.5%: at 1.5% the margin is 8px and at 0% Mali's westernmost point
    // is on the frame. The panel's aspect varies with the viewport, so re-measure rather than
    // reason about it — `path.gate-country` bounding boxes against `#gateArt`.
    map.fitBounds(bounds.isValid() ? bounds.pad(0.025) : HOME_BOUNDS, { animate: false });
    return () => {
      map.removeLayer(layer);
      setFootprint(null);
    };
  }, [map, countries]);

  // --- the land cover, mounted only once every frame has decoded --------------
  /**
   * Declared after the effect that fits the map and *dependent* on the `footprint` that
   * effect sets, so it can only run in a later commit — by which time Leaflet is fitted and
   * the overlay's geographic corners land where the reader will see them. Do not move this
   * effect up, and do not drop the `footprint` dependency: `footprint.all` is also the
   * outline this checks the committed artwork's registration against.
   *
   * ## Three gates, in order, and the same two outcomes as before
   *
   * 1. **Registration.** The frames were rendered to §1a's own outline of the four countries
   *    (`epochs.json.bbox_4326`). These are the outlines actually drawn. If they disagree by
   *    more than {@link BBOX_TOLERANCE}, the artwork is stale relative to the map and nothing
   *    is mounted — a wrong map is worse than no map.
   * 2. **Decode.** Every frame is loaded into a detached `<img>` *before* anything is added
   *    to Leaflet. That is the only way to know: this deployment's nginx answers a missing
   *    path with **HTTP 200 and `index.html`** (verified: 627 bytes, `text/html`), so
   *    `fetch().ok` is `true` for a frame that does not exist, and a CSS `background-image`
   *    has no load or error event at all. An `<img>`'s `error` event is the one honest
   *    signal, which is also why the frames must stay `<img>` elements all the way down.
   * 3. **Pixels.** A frame that decoded can still be empty. The render command enforces a
   *    54 % opaque floor at build time; this is the runtime half of it, because the committed
   *    file could be replaced by hand. When `data-lc` goes on, the green country fill retreats
   *    to zero (base.css) — so an empty frame set erases the four countries from the panel and
   *    leaves a CC-BY credit claiming data that is not there. That is a worse page than the
   *    server being down.
   *
   * A frame that fails any of the per-frame gates is simply dropped and the loop is built from
   * the survivors: a missing 2019 reads as a longer dwell on 2018 and nobody can tell. Below
   * {@link MIN_FRAMES} the whole stack is abandoned and the page is the approved one.
   */
  useEffect(() => {
    const el = containerRef.current;
    const set = EPOCHS;
    if (!map || !countries || !footprint || !set || !el) return;

    const [[south, west], [north, east]] = set.bounds;
    const drawn = footprint.all;
    const offBy =
      Math.max(
        Math.abs(drawn.getWest() - west),
        Math.abs(drawn.getEast() - east),
      ) > (east - west) * BBOX_TOLERANCE ||
      Math.max(
        Math.abs(drawn.getSouth() - south),
        Math.abs(drawn.getNorth() - north),
      ) > (north - south) * BBOX_TOLERANCE;
    if (offBy) return;

    const bounds = L.latLngBounds(set.bounds[0], set.bounds[1]);
    const decoded = new Map<number, HTMLImageElement>();
    const preloads: HTMLImageElement[] = [];
    const overlays: L.ImageOverlay[] = [];
    let pending = set.frames.length;
    let live = true;
    let settled = false;
    let mount = 0;
    let deadline = 0;

    /**
     * Did any frame that decoded actually paint a pixel?
     *
     * Each is drawn into a {@link PIXEL_PROBE_PX} square until one opaque pixel is found; a
     * healthy frame answers on the first. It **fails open** — no 2d context (jsdom) or a
     * tainted canvas returns "yes", leaving the decision where the decode gate left it —
     * because a probe that cannot see is not evidence of an empty frame set.
     */
    const carriesPixels = (frames: EpochFrame[]) => {
      let probe: CanvasRenderingContext2D | null = null;
      for (const frame of frames) {
        const image = decoded.get(frame.year);
        if (!image) continue;
        try {
          if (!probe) {
            const canvas = document.createElement('canvas');
            canvas.width = PIXEL_PROBE_PX;
            canvas.height = PIXEL_PROBE_PX;
            probe = canvas.getContext('2d', { willReadFrequently: true });
            if (!probe) return true;
          }
          probe.clearRect(0, 0, PIXEL_PROBE_PX, PIXEL_PROBE_PX);
          probe.drawImage(image, 0, 0, PIXEL_PROBE_PX, PIXEL_PROBE_PX);
          // Every fourth byte is an alpha channel; `some` short-circuits on the first opaque one.
          const { data } = probe.getImageData(0, 0, PIXEL_PROBE_PX, PIXEL_PROBE_PX);
          if (data.some((value, index) => index % 4 === 3 && value > 0)) return true;
        } catch {
          return true;
        }
      }
      return false;
    };

    /**
     * The decision, taken exactly once. `settled` guards the two paths that can reach it —
     * the last decode and the deadline — against each other; `live` guards both against an
     * unmount that has already happened.
     */
    const finish = () => {
      if (settled || !live) return;
      settled = true;
      window.clearTimeout(deadline);
      const usable = set.frames.filter((frame) => decoded.has(frame.year));
      if (usable.length < MIN_FRAMES || !carriesPixels(usable)) return;

      // Oldest on top, newest at the floor. The loop then runs as fade-*outs*, so the layer
      // under the one dissolving is always fully opaque and the composite is never less than
      // covered — see `layerKeyframes` for the arithmetic and for what a naive symmetric
      // cross-fade does instead.
      // The credit names the years that are actually painted, not the years that were baked:
      // a frame that failed to decode has been dropped from `usable` above, and a CC-BY line
      // claiming an epoch nobody can see is a wrong credit, not a generous one. The sidecar's
      // string is deliberately year-neutral so this is the only place the range is composed.
      const credit = set.attribution
        ? `${usable[0]?.year}–${usable[usable.length - 1]?.year} ${set.attribution}`
        : '';
      usable.forEach((frame, index) => {
        const newest = index === usable.length - 1;
        const overlay = L.imageOverlay(frame.src, bounds, {
          pane: 'gateLc',
          className: 'gate-epoch',
          // Decorative, inside an `aria-hidden` subtree; the year marker carries the meaning.
          alt: '',
          interactive: false,
          zIndex: usable.length - index,
          // On the newest overlay only, so the control lists it once. Leaflet registers it on
          // `addTo` and drops it on removal, which is exactly right: the credit is present
          // when the pixels are and absent when they are not.
          ...(newest && credit ? { attribution: credit } : {}),
        });
        overlay.addTo(map);
        const node = overlay.getElement();
        if (node) {
          node.dataset.year = String(frame.year);
          if (frame.srcSet) node.srcset = frame.srcSet;
          // The still state, and the state the loop starts and ends on: only the newest
          // epoch is opaque. Inline rather than in the stylesheet because it is per-layer,
          // and it is what `prefers-reduced-motion` falls back to when no animation is
          // constructed at all.
          if (!newest) node.style.opacity = '0';
        }
        overlays.push(overlay);
      });

      mount = window.setTimeout(() => {
        if (!live) return;
        el.setAttribute('data-lc', 'on');
        setPlaying(usable.map((frame) => frame.year));
      }, MOUNT_SETTLE_MS);
    };

    const settleOne = () => {
      pending -= 1;
      if (pending <= 0) finish();
    };

    deadline = window.setTimeout(finish, REVEAL_DEADLINE_MS);
    for (const frame of set.frames) {
      const image = new Image();
      image.decoding = 'async';
      // **`srcset` before `src`, and the same `srcset` the overlay will carry.** The overlay
      // below is given `node.srcset = frame.srcSet` after Leaflet has set its `src`, so on a
      // 2× display the browser re-selects and paints the `@2x` file. A preload that only ever
      // asked for the 1× URL would therefore have decoded a *different* image from the one
      // that reaches the panel, and the all-or-nothing reveal would be vouching for a file it
      // never saw. Inert for the committed 1× set (`srcSet` is null), which is why it has to
      // be written now rather than when `--dpr 1 2` is first shipped.
      if (frame.srcSet) image.srcset = frame.srcSet;
      image.onload = () => {
        // Guard against the browser reporting a load for something that is not an image —
        // the 200-and-HTML case decodes to nothing, and `naturalWidth` is how that shows.
        if (image.naturalWidth > 0) decoded.set(frame.year, image);
        settleOne();
      };
      image.onerror = settleOne;
      image.src = frame.src;
      preloads.push(image);
    }

    return () => {
      live = false;
      window.clearTimeout(deadline);
      window.clearTimeout(mount);
      // Detach before the elements are dropped: an in-flight decode that completes after the
      // component is gone must not reach a closure over a removed map.
      for (const image of preloads) {
        image.onload = null;
        image.onerror = null;
      }
      for (const overlay of overlays) map.removeLayer(overlay);
      el.removeAttribute('data-lc');
      setPlaying(null);
    };
  }, [map, countries, footprint]);

  // --- the loop --------------------------------------------------------------
  /**
   * Separate from the effect above because it needs the year marker, and the marker is
   * React's to render from `playing` — so it does not exist until the commit *after* the
   * stack mounts. Splitting them is also what lets the whole loop be rebuilt on a
   * `prefers-reduced-motion` change without touching the raster.
   *
   * Everything animated here is `opacity` or `transform`, so every animation runs in the
   * compositor: measured at 6× CPU throttling across a dissolve, 218 rAF samples, mean
   * 8.28 ms, p95 9.3 ms, zero frames over 33 ms.
   *
   * `window.matchMedia` is **guarded**. It is undefined in this project's jsdom and an
   * unguarded call takes every test in `SignInGate.test.tsx` with it; where it is absent the
   * loop simply runs, which is the right default for a browser old enough not to have it.
   */
  useEffect(() => {
    const el = containerRef.current;
    if (!playing || playing.length < MIN_FRAMES || !el) return;
    const marker = markerRef.current;
    const query =
      typeof window.matchMedia === 'function'
        ? window.matchMedia('(prefers-reduced-motion: reduce)')
        : null;
    const animations: Animation[] = [];

    const build = () => {
      const images = Array.from(el.querySelectorAll<HTMLImageElement>('img.gate-epoch')).sort(
        (a, b) => Number(a.dataset.year) - Number(b.dataset.year),
      );
      const years = images.length;
      // `animate` is feature-detected rather than assumed: a browser without Web Animations
      // keeps the still newest-epoch picture, which is the page the client already approved.
      if (years < MIN_FRAMES || typeof images[0]?.animate !== 'function') return;
      const timing: KeyframeAnimationOptions = {
        duration: loopMs(years),
        // Held back until the reveal's own fade is over: two motions at once on the first
        // second of the sign-in page is one too many, and the opening hold exists so the
        // reader meets the still picture first.
        delay: REVEAL_FADE_MS,
        iterations: ITERATIONS,
        // `both`: during the delay and after the last iteration the elements hold the
        // loop's first and last keyframes, which are the same picture — the newest epoch,
        // the marker on its last year. So the panel comes to rest exactly where the still
        // page would have been, with no snap.
        fill: 'both',
      };
      // The newest epoch is the floor of the stack and is never animated.
      images.slice(0, -1).forEach((node, index) => {
        animations.push(node.animate(layerKeyframes(index, years), timing));
      });
      const strip = marker?.querySelector<HTMLElement>('.gate-epoch-strip');
      const dot = marker?.querySelector<HTMLElement>('.gate-epoch-dot');
      const fill = marker?.querySelector<HTMLElement>('.gate-epoch-fill');
      if (strip) animations.push(strip.animate(odometerKeyframes(years), timing));
      if (dot) animations.push(dot.animate(dotKeyframes(years), timing));
      if (fill) animations.push(fill.animate(fillKeyframes(years), timing));
      // The sweep is the only thing on this panel that a reader sees without looking for it;
      // see `sweepKeyframes`. Driven from here rather than by CSS so it is cut from the same
      // timeline as the dissolve it belongs to — a band that arrives half a second off the
      // year it is announcing reads as decoration.
      const sweep = sweepRef.current;
      if (sweep) animations.push(sweep.animate(sweepKeyframes(years), timing));
      if (document.visibilityState === 'hidden') {
        for (const animation of animations) animation.pause();
      }
    };

    const teardown = () => {
      // `cancel`, not `finish`: cancelling drops the fill and every element falls back to the
      // rest state its own stylesheet or inline style carries — which is the composed still
      // frame, not a half-faded one.
      for (const animation of animations.splice(0)) animation.cancel();
    };

    const sync = () => {
      teardown();
      if (!query?.matches) build();
    };

    /**
     * A hidden tab renders no frames, so a compositor animation is already doing no work
     * there — but its clock keeps running, and a reader who comes back after two minutes
     * would find the loop finished and the story untold. Pausing is what makes "three loops"
     * mean three loops the reader actually saw.
     */
    const onVisibility = () => {
      const hidden = document.visibilityState === 'hidden';
      for (const animation of animations) {
        // `playState` is `finished`, not `paused`, once the three iterations are done — so a
        // settled panel is never restarted by coming back to the tab.
        if (hidden) animation.pause();
        else if (animation.playState === 'paused') animation.play();
      }
    };

    sync();
    document.addEventListener('visibilitychange', onVisibility);
    query?.addEventListener?.('change', sync);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      query?.removeEventListener?.('change', sync);
      teardown();
    };
  }, [playing]);

  if (!countries) return null;

  const years = playing && playing.length >= MIN_FRAMES ? playing : null;
  const newest = years ? years[years.length - 1] : null;

  return (
    <>
      <div className="gate-art-map" data-gatemap="" aria-hidden="true">
        <div ref={containerRef} data-gatemapcanvas="" />
      </div>
      {/*
        The light sweep. Rendered only when the frames actually loaded — the same `years` gate
        the marker uses — so the no-frames fallback is exactly the still page the client already
        approved, with nothing sliding across it.

        A **sibling** of the map rather than a child, so it is not inside `[data-gatemap]`'s
        `pointer-events:none` subtree by accident and, more importantly, so
        `.gate-art-map>div{width:100%;height:100%}` does not claim it. Its place in the paint
        order is the whole point and it is bought by DOM order, not by a z-index race: this is
        `z-index:1` and so is `.gate-art-wash`, which comes later in `SignInGate`, so the wash
        always paints over the band. See `.gate-sweep` in base.css.
      */}
      {years ? <div className="gate-sweep" ref={sweepRef} aria-hidden="true" /> : null}
      {/*
        The year marker — the client's *"they just want to see that dynamism playing out"*, made
        legible. Turn it off and eight years of land cover read as shimmer: consecutive epochs
        differ by 2.6-4.5 % of data pixels, which is real change and is still not something a
        reader notices on a page they are about to type a password into. The marker is what
        converts those pixels into evidence, which is the client's own reason for wanting the
        map here at all.

        It is a **sibling of the map, not a child of it**, for two reasons. `[data-gatemap]` is
        `aria-hidden` with `pointer-events:none` — right for scenery, wrong for the one piece of
        this panel that states a fact, so the caption below is real text a screen reader reads
        while the spinning number is hidden from it. And nothing here is a control: no handle,
        no groove, no hover state. A marker that looks like a slider on an inert map invites a
        click that does nothing.
      */}
      {years && newest ? (
        <div className="gate-epoch-marker" data-gateyear="" ref={markerRef}>
          <div className="gate-epoch-odo" aria-hidden="true">
            {/*
              An odometer, not a cross-fade. Eight numbers stacked in one box and dissolved the
              way the raster is renders 2020 over 2024 as "2029" — so the years ride a strip
              that is clipped to one line, and at most two are ever partly visible. The newest
              year is repeated at the head of the strip so the rewind is one adjacent step
              rather than a spin through seven numbers nobody asked to see.
            */}
            <div className="gate-epoch-strip">
              {[newest, ...years].map((year, index) => (
                <span key={`${year}-${index}`}>{year}</span>
              ))}
            </div>
          </div>
          <div className="gate-epoch-rail" aria-hidden="true">
            <span className="gate-epoch-fill" />
            <span className="gate-epoch-ticks">
              {years.map((year) => (
                <span key={year} />
              ))}
            </span>
            <span className="gate-epoch-dot" />
          </div>
          <p className="gate-epoch-cap">
            {t('gate.landCover')} · {years[0]}–{newest}
          </p>
        </div>
      ) : null}
    </>
  );
}

export default GateMap;
